import { RadioGroup as RadioGroupArk } from '@ark-ui/react'
import { useMemo } from 'react'
import LockIcon from '../CustomIcons/LockIcon'
import SelectedCheckmarkIcon from '../CustomIcons/SelectedCheckmarkIcon'
import { cn } from '../utlis/cn'
import { MenuItem } from './MenuButton/MenuButton'

type RadioGroupItem = {
    label: string
    value: string
    disabled?: boolean
    locked?: boolean
    tooltip?: string
    menuItem?: boolean
    onClick?: () => void
}

type RadioGroupProps = {
    items: readonly RadioGroupItem[]
    defaultValue?: string
    value?: string
    onChange: (value: string | null) => void
    disabled?: boolean
    className?: string
    groupLabel?: string | null
    labelClassName?: string | null
}

export function RadioGroup({
    items,
    value,
    onChange,
    disabled,
    className,
    defaultValue,
    groupLabel,
    labelClassName,
}: RadioGroupProps) {
    function renderItem(item: RadioGroupItem) {
        const itemEl = (
            <RadioOption
                key={item.value}
                label={item.label}
                value={item.value}
                checked={value === item.value}
                disabled={item.disabled}
                locked={item.locked}
                tooltip={item.tooltip}
                onClick={item.onClick}
            />
        )

        if (item.menuItem) {
            return <MenuItem key={item.value}>{itemEl}</MenuItem>
        }
        return itemEl
    }
    return (
        <RadioGroupArk.Root
            className={cn('flex flex-col gap-4', className)}
            onValueChange={(details) => onChange(details.value)}
            defaultValue={defaultValue}
            value={value}
        >
            {groupLabel && <RadioGroupArk.Label className={cn(labelClassName)}>{groupLabel}</RadioGroupArk.Label>}
            {items.map((item) => renderItem(item))}
        </RadioGroupArk.Root>
    )
}

type RadioOptionProps = {
    label: string
    value: string
    checked: boolean
    disabled?: boolean
    locked?: boolean
    tooltip?: string
    onClick?: () => void
}

function RadioOption({ label, value, checked, disabled, locked, tooltip, onClick }: RadioOptionProps) {
    const control = useMemo(() => {
        if (checked) {
            const gradient = 'bg-linear-110 from-[#FCE640] from-20% via-[#F49629] via-70% to-[#F17E22] to-100%'
            return (
                <RadioGroupArk.ItemControl
                    className={cn(
                        'w-[20px] h-[20px] rounded-full text-black flex items-center justify-center',
                        gradient
                    )}
                >
                    <SelectedCheckmarkIcon style={{ fontSize: '1.3rem' }} />
                </RadioGroupArk.ItemControl>
            )
        } else if (locked) {
            return (
                <RadioGroupArk.ItemControl className="w-[20px] h-[20px]">
                    <LockIcon style={{ fontSize: '1.3rem' }} />
                </RadioGroupArk.ItemControl>
            )
        }
        return <RadioGroupArk.ItemControl className="w-[20px] h-[20px] rounded-full border border-white/15" />
    }, [checked, disabled, locked])

    return (
        <RadioGroupArk.Item
            key={value}
            value={value}
            disabled={disabled || locked}
            className="flex gap-2 items-center cursor-pointer"
            onClick={onClick}
        >
            {control}
            <RadioGroupArk.ItemText
                className={cn({ 'font-light': !checked, 'opacity-75': locked || disabled || !checked })}
            >
                {label}
            </RadioGroupArk.ItemText>
            <RadioGroupArk.ItemHiddenInput />
        </RadioGroupArk.Item>
    )
}
